/* Copyright 2016-2022
   Kabandy
   Author: Cedric Hoarau
 */

import { ApiRouteFormatter, getApiKey, getApiVersion, isCloudRunEnabled, setApiVersion, setCloudRunEnabled } from "../ApiWeb";
import { UrlParam } from "../System";
import { OAuthClientConf } from "./oauth-client-conf";

let _rootDiv: HTMLDivElement | undefined = undefined;
let _currentApiRoutes: HTMLUListElement | undefined = undefined;

export namespace UIApi {
    export function Bind() {
        document.body.appendChild( _InitializeUI() )
    }

    function _InitializeUI(): HTMLDivElement {
        _rootDiv = document.createElement("div");
        _rootDiv.id = "ui-api-root-div";

        let titleELement = document.createElement("h2");
        titleELement.innerText = "Kabandy Platform";
        _rootDiv.appendChild(titleELement);

        // Debug mode
        const isDebugMode: boolean = UrlParam.Has(window.location.search, "debug");
        if( isDebugMode ) {
            // No cloud run check box
            const checkboxElement: HTMLInputElement = document.createElement("input");
            checkboxElement.id = "ui-api-no-cloud-run-check-box";
            checkboxElement.setAttribute("type", "checkbox");

            const labelElement: HTMLLabelElement = document.createElement("label");
            labelElement.textContent = "Use cloud run";
            labelElement.setAttribute("for", checkboxElement.id);

            checkboxElement.checked = isCloudRunEnabled();

            _rootDiv.appendChild(checkboxElement);
            _rootDiv.appendChild(labelElement);
            _rootDiv.appendChild(document.createElement("br"));

            // API version
            const apiVersionInputElement: HTMLInputElement = document.createElement("input");
            apiVersionInputElement.readOnly = true;
            apiVersionInputElement.style.backgroundColor = "#f0f0f0";
            apiVersionInputElement.style.color = "#888";
            apiVersionInputElement.value = getApiVersion();
            const versionLabelElement = document.createElement("label");
            versionLabelElement.textContent = "API Version";
            versionLabelElement.setAttribute("for", apiVersionInputElement.id);
            _rootDiv.appendChild(document.createElement("br"));
            _rootDiv.appendChild(versionLabelElement);
            _rootDiv.appendChild(apiVersionInputElement);

            function onVersionChanged() {
                // @ts-ignore
                const sanitizedValue: string = DOMPurify.sanitize(apiVersionInputElement.value, { USE_PROFILES: { html: false } });
                setApiVersion(sanitizedValue);
                _currentApiRoutes = _RefreshUIRoutes(_currentApiRoutes, ApiRouteFormatter.FormatApiRoutes(isCloudRunEnabled(), getApiVersion()));
            }
            apiVersionInputElement.addEventListener("change", onVersionChanged);

            // API routes
            _currentApiRoutes = _CreateUIRoutes(ApiRouteFormatter.FormatApiRoutes(isCloudRunEnabled(), getApiVersion()));
            _rootDiv.appendChild(_currentApiRoutes);

            // click handler
            function onNoCloudRunChanged() {
                setCloudRunEnabled(checkboxElement.checked);
                _currentApiRoutes = _RefreshUIRoutes(_currentApiRoutes, ApiRouteFormatter.FormatApiRoutes(isCloudRunEnabled(), getApiVersion()));
            }
            checkboxElement.addEventListener("click", onNoCloudRunChanged);

            // ApiKey
            const apiKey : string = getApiKey();
            if( apiKey ) {
                const apiKeyInputElement: HTMLInputElement = document.createElement("input");
                apiKeyInputElement.readOnly = true;
                apiKeyInputElement.style.backgroundColor = "#f0f0f0";
                apiKeyInputElement.style.color = "#888";
                apiKeyInputElement.id = "ui-api-key-input";
        
                const apiKeyLabelElement = document.createElement("label");
                apiKeyLabelElement.textContent = "API Key";
                apiKeyLabelElement.setAttribute("for", apiKeyInputElement.id);
                apiKeyInputElement.value = apiKey;
        
                _rootDiv.appendChild(apiKeyLabelElement);
                _rootDiv.appendChild(apiKeyInputElement);
                _rootDiv.appendChild(document.createElement("br"));
            } 

            // Client ID
            const clientId: string = OAuthClientConf.ClientId();
            if( clientId ) 
            {
                const clientIdInputElement: HTMLInputElement = document.createElement("input");
                clientIdInputElement.id = "ui-api-client-id-input";
                clientIdInputElement.readOnly = true;
                clientIdInputElement.style.backgroundColor = "#f0f0f0";
                clientIdInputElement.style.color = "#888";
                clientIdInputElement.style.width = "13%";
        
                const clientIdLabelElement = document.createElement("label");
                clientIdLabelElement.textContent = "Client ID";
                clientIdLabelElement.setAttribute("for", clientIdInputElement.id);
                clientIdInputElement.value = clientId;
        
                _rootDiv.appendChild(clientIdLabelElement);
                _rootDiv.appendChild(clientIdInputElement);
                _rootDiv.appendChild(document.createElement("br"));
            }

            // Client Secret
            const clientSecret: string = OAuthClientConf.ClientSecret();
            if( clientSecret ) 
            {
                const clientSecretInputElement: HTMLInputElement = document.createElement("input");
                clientSecretInputElement.id = "ui-api-client-secret-input";
                clientSecretInputElement.readOnly = true;
                clientSecretInputElement.style.backgroundColor = "#f0f0f0";
                clientSecretInputElement.style.color = "#888";
                clientSecretInputElement.style.width = "13%";
        
                const clientSecretLabelElement = document.createElement("label");
                clientSecretLabelElement.textContent = "Client SECRET";
                clientSecretLabelElement.setAttribute("for", clientSecretInputElement.id);
                clientSecretInputElement.value = clientSecret;
        
                _rootDiv.appendChild(clientSecretLabelElement);
                _rootDiv.appendChild(clientSecretInputElement);
                _rootDiv.appendChild(document.createElement("br"));
            }
        }
        // Release mode
        else {
            const mainRoutesHtmlListElement: HTMLUListElement = _CreateUIReleaseMainRoutes( 
                ApiRouteFormatter.FormatAccountsUrl(isCloudRunEnabled()),
                ApiRouteFormatter.FormatApiGatewayUrl(isCloudRunEnabled()),
                ApiRouteFormatter.FormatViewerUrl(isCloudRunEnabled(), false),
            );
            _rootDiv.appendChild(mainRoutesHtmlListElement);
        }

        return _rootDiv;
    }
    function _CreateUIRoutes(routes: string[] = []): HTMLUListElement {
        const oList = document.createElement("ul");

        for (let route of routes) {
            let cListElement = document.createElement("li");
            let cHrElement = document.createElement("a")
            cHrElement.href = route;
            // @ts-ignore
            cHrElement.innerHTML = DOMPurify.sanitize(route, { USE_PROFILES: { html: false } });
            // cHrElement.textContent = route;
            cListElement.appendChild(cHrElement)
            oList.appendChild(cListElement);
        }

        return oList;
    }
    function _RefreshUIRoutes(currentList?: HTMLUListElement, routes: string[] = []): HTMLUListElement | undefined {
        let oNextList: HTMLUListElement | undefined = undefined;

        if (currentList) {
            const nextList = _CreateUIRoutes(routes);

            const parentElement: HTMLElement | null = currentList.parentElement;
            if (parentElement) {
                parentElement.replaceChild(nextList, currentList);
                oNextList = nextList;
            }
        }

        return oNextList;
    }
    function _CreateUIReleaseMainRoutes( accountsUrl : string, apiUrl : string, viewerUrl : string ): HTMLUListElement  {
        const oList = document.createElement("ul");

        // Accounts
        let accountsListElement = document.createElement("li");
        let accountsSpanElement = document.createElement("span");
        accountsSpanElement.textContent = "Accounts -> ";
        accountsListElement.appendChild(accountsSpanElement);
        let accountsAElement = document.createElement("a");
        accountsAElement.href = accountsUrl;
        accountsAElement.textContent = accountsUrl;
        accountsListElement.appendChild(accountsAElement);
        oList.appendChild(accountsListElement);

        // API
        let apiListElement = document.createElement("li");
        let apiSpanElement = document.createElement("span");
        apiListElement.textContent = "API -> ";
        accountsListElement.appendChild(apiSpanElement);
        let apiAElement = document.createElement("a")
        apiAElement.href = apiUrl + "/v1/docs";
        apiAElement.textContent = apiUrl + "/v1/docs";
        apiListElement.appendChild(apiAElement);
        oList.appendChild(apiListElement);

        // Viewer
        let viewerListElement = document.createElement("li");
        let viewerSpanElement = document.createElement("span");
        viewerListElement.textContent = "Viewer -> ";
        viewerListElement.appendChild(viewerSpanElement);
        let viewerAElement = document.createElement("a")
        viewerAElement.href = viewerUrl;
        viewerAElement.textContent = viewerUrl;
        viewerListElement.appendChild(viewerAElement);
        oList.appendChild(viewerListElement);

        return oList;
    }
}
